import styled, { css } from 'styled-components'

import { media } from '../../../utils/mediaQuery'

// Container
export const DataTableContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
`

// Header
export const DataTableHeader = styled.div`
  padding: 0px 0px 20px 20px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;

  ${media.tabletDown} {
    display: none;
  }

  @media print {
    display: none !important;
  }
`

export const DataTableActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  align-items: center;
`

// Column Settings
export const ColumnSettingsBtn = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};

  svg {
    width: 13px;
    height: 13px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 1023px) {
    padding: ${({ theme }) => theme.spacing.small};
    justify-content: center;

    /* Hide text on mobile, show only icon */
    span {
      display: none;
    }
  }
`

export const ColumnDropdown = styled.div<{ $active: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: ${({ theme }) => theme.spacing.xsmall};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.default};
  z-index: 1000;
  min-width: 240px;
  max-height: 400px;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.medium};
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  visibility: ${({ $active }) => ($active ? 'visible' : 'hidden')};
  transform: translateY(${({ $active }) => ($active ? 0 : '-10px')});
  transition: all ${({ theme }) => theme.transitions.default};
`

export const ColumnDropdownHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

export const ColumnDropdownHint = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

export const ColumnItem = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.xsmall} 0;
  cursor: pointer;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.primary};
  /* Ensure minimum touch target height */
  min-height: 29px;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  /* Mobile: Ensure 44px minimum touch target */
  @media (max-width: 1023px) {
    min-height: ${({ theme }) => theme.touchTargets.minimum};
    padding: ${({ theme }) => theme.spacing.small} 0;
  }
`

export const ColumnCheckbox = styled.input`
  accent-color: ${({ theme }) => theme.colors.primary};
  /* Larger checkbox for better touch interaction */
  width: 15px;
  height: 15px;
  cursor: pointer;

  /* Mobile: Ensure checkbox is large enough to tap */
  @media (max-width: 1023px) {
    width: 16px;
    height: 16px;
  }
`

export const ColumnLabel = styled.span`
  flex: 1;
`

// Table
export const TableWrapper = styled.div<{
  $stickyHeader?: boolean
  $classicScrollbars?: boolean
}>`
  flex: 1;
  overflow: auto;
  position: relative;
  min-height: 0; /* Important for flex child scrolling */
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-bottom: none;
  border-radius: ${({ theme }) => theme.borderRadius.medium}
    ${({ theme }) => theme.borderRadius.medium} 0 0;
  -webkit-overflow-scrolling: touch;

  /* Scrollbars. The app hides every scrollbar globally (GlobalStyles:
   * '::-webkit-scrollbar { width: 0 }' + '* { scrollbar-width: none }'),
   * but data tables routinely overflow horizontally and a mouse user had
   * no way to tell, let alone drag (Aldo 2026-05). We bring the bar back
   * ONLY when it should be there, driven by '$classicScrollbars' (see
   * useClassicScrollbars): true when the OS renders classic, space-taking
   * scrollbars — i.e. a real mouse is attached — false for a trackpad,
   * where the OS uses overlay scrollbars. CSS can't tell mouse from
   * trackpad ('pointer: fine' matches both), hence the JS probe.
   *
   *   - $classicScrollbars (mouse): always-on custom bars. Horizontal is
   *     the loud Popety-blue one (the axis that overflows unexpectedly);
   *     vertical is a quiet neutral grey so it reads but doesn't compete.
   *   - otherwise (trackpad/touch): no custom bars — 'scrollbar-width:
   *     thin' restores the native overlay bars that appear only while
   *     scrolling and fade out at rest.
   *
   * Two WebKit gotchas baked into the rules below:
   *   1. Chrome 121+ honours '::-webkit-scrollbar' only when the standard
   *      'scrollbar-width'/'scrollbar-color' are at their initial 'auto'.
   *      So the mouse branch uses 'scrollbar-width: auto'; any non-initial
   *      value (e.g. the trackpad branch's 'thin') switches Chrome to
   *      standard rendering and ignores the webkit rules.
   *   2. Per-axis SIZING must live on the base '::-webkit-scrollbar'
   *      (width = vertical, height = horizontal); the ':horizontal' /
   *      ':vertical' pseudo-classes only reliably drive per-axis COLOUR. */

  ${({ $classicScrollbars, theme }) =>
    $classicScrollbars
      ? css`
          /* Mouse connected (OS in classic-scrollbar mode): always-on
           * custom bars. Custom '::-webkit-scrollbar' bars are persistent
           * (never overlay), which is exactly the "always display for
           * mouse users" we want. 'scrollbar-width: auto' keeps WebKit on
           * the pseudo-element path (initial value) so the rules below are
           * honoured. Sizing is on the base element (width = vertical,
           * height = horizontal); per-axis colour via ':horizontal' /
           * ':vertical'. Horizontal = Popety blue (the discoverability
           * win); vertical = neutral grey so only horizontal stands out. */
          scrollbar-width: auto;

          &::-webkit-scrollbar {
            width: 10px;
            height: 12px;
          }

          /* Horizontal — Popety blue */
          &::-webkit-scrollbar-track:horizontal {
            background: ${theme.colors.primaryLight};
            border-radius: ${theme.borderRadius.small};
          }
          &::-webkit-scrollbar-thumb:horizontal {
            background: ${theme.colors.primary};
            border: 2px solid transparent;
            background-clip: padding-box;
            border-radius: ${theme.borderRadius.medium};
          }
          &::-webkit-scrollbar-thumb:horizontal:hover {
            background: ${theme.colors.primaryHover};
            background-clip: padding-box;
          }

          /* Vertical — neutral grey */
          &::-webkit-scrollbar-track:vertical {
            background: transparent;
          }
          &::-webkit-scrollbar-thumb:vertical {
            background: ${theme.colors.border.default};
            border: 2px solid transparent;
            background-clip: padding-box;
            border-radius: ${theme.borderRadius.medium};
          }
          &::-webkit-scrollbar-thumb:vertical:hover {
            background: ${theme.colors.text.tertiary};
            background-clip: padding-box;
          }

          &::-webkit-scrollbar-corner {
            background: transparent;
          }
        `
      : css`
          /* Trackpad / touch (OS in overlay-scrollbar mode): we want the
           * native overlay bars that fade in only while scrolling. Setting
           * a NON-initial 'scrollbar-width' makes Chrome render the
           * standard scrollbar and ignore the global
           * '::-webkit-scrollbar { width: 0 }' hide rule — so the OS
           * overlay scrollbars come back instead of being suppressed.
           * No custom colour: at rest there's no bar at all. */
          scrollbar-width: thin;
        `}

  @media print {
    overflow: visible !important;
    border: none !important;
  }

  ${({ $stickyHeader }) =>
    $stickyHeader &&
    css`
      thead {
        position: sticky;
        top: 0;
        z-index: 10;
        background: ${({ theme }) => theme.colors.background.secondary};
        height: 48px; /* Adjust based on your header height */
      }
    `}

  /* Mobile: Horizontal scroll shadow indicators */
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    /* Scroll shadow container */
    &::before,
    &::after {
      content: '';
      position: sticky;
      top: 0;
      bottom: 0;
      width: 24px;
      pointer-events: none;
      z-index: 5;
      transition: opacity 0.2s ease;
    }

    /* Left shadow - indicates more content on left */
    &::before {
      left: 0;
      background: linear-gradient(
        to right,
        ${({ theme }) => theme.colors.background.primary},
        transparent
      );
      box-shadow: inset 12px 0 8px -8px rgba(0, 0, 0, 0.1);
    }

    /* Right shadow - indicates more content on right */
    &::after {
      right: 0;
      background: linear-gradient(
        to left,
        ${({ theme }) => theme.colors.background.primary},
        transparent
      );
      box-shadow: inset -12px 0 8px -8px rgba(0, 0, 0, 0.1);
    }
  }
`

export const Table = styled.table<{
  $bordered?: boolean
  $hover?: boolean
  $compact?: boolean
  $dense?: boolean
}>`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  ${({ $bordered, theme }) =>
    $bordered &&
    css`
      border: 1px solid ${theme.colors.border.light};

      th,
      td {
        border: 1px solid ${theme.colors.border.light};
      }
    `}

  ${({ $hover }) =>
    $hover &&
    css`
      tbody tr:hover {
        background: ${({ theme }) => theme.colors.background.secondary};
      }
    `}

  ${({ $compact }) =>
    $compact &&
    css`
      th,
      td {
        padding: ${({ theme }) => theme.spacing.small}
          ${({ theme }) => theme.spacing.medium};
      }
    `}

  ${({ $dense }) =>
    $dense &&
    css`
      th,
      td {
        padding: ${({ theme }) => theme.spacing.xsmall}
          ${({ theme }) => theme.spacing.small};
        font-size: 10px;
        line-height: 1.2;
      }

      th {
        font-size: 8px;
      }
    `}

  @media print {
    tr {
      page-break-inside: avoid;
    }
  }
`

export const TableHead = styled.thead`
  @media print {
    display: table-header-group;
  }
`

export const TableHeader = styled.th<{ $hideOnMobile?: boolean }>`
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  text-align: left;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  user-select: none;
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.sortable {
    cursor: pointer;
    /* Reserve room for the indicator so the label width is stable
       whether or not this column is the active sort. */
    padding-right: 18px;

    &:hover {
      color: ${({ theme }) => theme.colors.text.primary};
    }

    /* Default (inactive) indicator: faint stacked up/down chevrons.
       Active asc / desc states override the colour + collapse to a
       single solid arrow via the rules below. */
    &::after {
      content: '';
      position: absolute;
      right: 6px;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      border-bottom: 4px solid ${({ theme }) => theme.colors.text.tertiary};
      opacity: 0.55;
    }

    /* The second (paired) chevron, only drawn in the inactive state.
       In asc/desc we hide it so the active arrow reads cleanly. */
    &::before {
      content: '';
      position: absolute;
      right: 6px;
      top: 50%;
      transform: translateY(2px);
      width: 0;
      height: 0;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      border-top: 4px solid ${({ theme }) => theme.colors.text.tertiary};
      opacity: 0.55;
    }

    &.sort-asc::before,
    &.sort-desc::before {
      content: none;
    }

    &.sort-asc::after,
    &.sort-desc::after {
      content: '';
      position: absolute;
      right: 6px;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      opacity: 1;
    }

    &.sort-asc::after {
      border-bottom: 6px solid ${({ theme }) => theme.colors.primary};
      border-top: 0;
    }

    &.sort-desc::after {
      border-top: 6px solid ${({ theme }) => theme.colors.primary};
      border-bottom: 0;
    }
  }

  ${media.tabletDown} {
    ${({ $hideOnMobile }) =>
      $hideOnMobile &&
      css`
        display: none;
      `}
  }

  @media print {
    padding-top: 40px !important;
  }
`

export const TableBody = styled.tbody``

export const TableFoot = styled.tfoot`
  display: none;

  @media print {
    display: table-footer-group;

    td {
      height: 30px;
      padding: 0;
      border: none;
    }
  }
`

export const TableRow = styled.tr<{ $selected?: boolean }>`
  background: ${({ theme }) => theme.colors.background.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  transition: background ${({ theme }) => theme.transitions.default};
  cursor: pointer;

  ${({ $selected, theme }) =>
    $selected &&
    css`
      background: ${theme.colors.primaryLight};
    `}

  &:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  }

  @media print {
    td {
      padding-bottom: 12px !important;
    }
  }
`

export const TableCell = styled.td<{ $hideOnMobile?: boolean }>`
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;

  ${media.tabletDown} {
    ${({ $hideOnMobile }) =>
      $hideOnMobile &&
      css`
        display: none;
      `}
  }
`

// Checkbox cell
export const CheckboxCell = styled(TableCell)`
  width: 38px;
  padding: 0;
  text-align: center;

  /* Ensure checkbox input has adequate touch target */
  input[type='checkbox'] {
    width: 15px;
    height: 15px;
    cursor: pointer;
    accent-color: ${({ theme }) => theme.colors.primary};
  }

  /* Mobile: Larger touch target for row selection */
  @media (max-width: 1023px) {
    width: 42px;

    input[type='checkbox'] {
      width: 16px;
      height: 16px;
    }
  }
`

export const CheckboxCellHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  min-height: 35px;

  /* Ensure checkbox input has adequate touch target */
  input[type='checkbox'] {
    width: 15px;
    height: 15px;
    cursor: pointer;
    accent-color: ${({ theme }) => theme.colors.primary};
  }

  /* Mobile: Larger touch target for header checkbox */
  @media (max-width: 1023px) {
    width: 42px;

    input[type='checkbox'] {
      width: 16px;
      height: 16px;
    }
  }
`

// Pagination
export const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
  padding: ${({ theme }) => theme.spacing.medium}
    ${({ theme }) => theme.spacing.large};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.secondary};
  flex-shrink: 0;
  border-radius: 0 0 ${({ theme }) => theme.borderRadius.medium}
    ${({ theme }) => theme.borderRadius.medium};

  ${media.tabletDown} {
    padding: ${({ theme }) => theme.spacing.xsmall}
      ${({ theme }) => theme.spacing.small};
    gap: ${({ theme }) => theme.spacing.xsmall};
    justify-content: space-between;
    border-radius: 0;
    border-left: none;
    border-right: none;
    border-bottom: none;
  }

  ${media.mobileOnly} {
    /* Keep pagination inside the table card. The complete page-number strip,
       four navigation buttons and page-size select cannot coexist as 44px
       touch targets on a 320–390px viewport. */
    overflow: hidden;
  }
`

export const PaginationInfo = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};

  strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }

  ${media.tabletDown} {
    font-size: ${({ theme }) => theme.typography.fontSize.micro};
    white-space: nowrap;
    flex-shrink: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

export const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};

  ${media.tabletDown} {
    gap: 2px;
  }

  ${media.mobileOnly} {
    flex: 0 0 auto;

    /* Previous/next are sufficient on phones. First/last and the numbered
       strip remain available from tablet widths upward. */
    > button:first-of-type,
    > button:last-of-type {
      display: none;
    }
  }
`

export const PaginationBtn = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};

  svg {
    width: 13px;
    height: 13px;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.background.secondary};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${media.tabletDown} {
    width: 23px;
    height: 23px;
    border-radius: ${({ theme }) => theme.borderRadius.small};

    svg {
      width: 12px;
      height: 12px;
    }
  }

  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.background.primary};
      border-color: ${({ theme }) => theme.colors.border.default};
    }

    &:active:not(:disabled) {
      background: ${({ theme }) => theme.colors.background.secondary};
    }
  }
`

export const PaginationPages = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xsmall};

  ${media.tabletDown} {
    gap: 2px;
  }

  ${media.mobileOnly} {
    display: none;
  }
`

export const PaginationPage = styled.button<{ $active: boolean }>`
  min-width: 26px;
  height: 26px;
  padding: 0 ${({ theme }) => theme.spacing.small};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.background.primary};
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.primary : theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.text.inverse : theme.colors.text.primary};
  font-size: 11px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};

  &:hover:not(:disabled) {
    background: ${({ $active, theme }) =>
      $active ? theme.colors.primary : theme.colors.background.secondary};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  ${media.tabletDown} {
    min-width: 23px;
    height: 23px;
    padding: 0 ${({ theme }) => theme.spacing.xsmall};
    font-size: ${({ theme }) => theme.typography.fontSize.micro};
  }

  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover:not(:disabled) {
      background: ${({ $active, theme }) =>
        $active ? theme.colors.primary : theme.colors.background.primary};
      border-color: ${({ $active, theme }) =>
        $active ? theme.colors.primary : theme.colors.border.default};
    }

    &:active:not(:disabled) {
      background: ${({ $active, theme }) =>
        $active ? theme.colors.primary : theme.colors.background.secondary};
    }
  }
`

export const PaginationPerPage = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  flex: 0 0 auto;

  ${media.tabletDown} {
    gap: 4px;
  }
`

export const PerPageLabel = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};

  ${media.tabletDown} {
    display: none;
  }
`

export const PerPageSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.xsmall}
    ${({ theme }) => theme.spacing.small};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  ${media.tabletDown} {
    font-size: ${({ theme }) => theme.typography.fontSize.micro};
    padding: 3px 5px;
    min-height: 23px;
  }
`

// Bulk Actions
export const BulkActionsBar = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: ${({ theme }) => theme.spacing.medium}
    ${({ theme }) => theme.spacing.large};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.large};
  z-index: 1000;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translate(-50%, 100%);
      opacity: 0;
    }
    to {
      transform: translate(-50%, 0);
      opacity: 1;
    }
  }

  ${media.tabletDown} {
    bottom: 16px;
    left: 16px;
    right: 16px;
    transform: none;
    padding: ${({ theme }) => theme.spacing.small}
      ${({ theme }) => theme.spacing.medium};
    gap: ${({ theme }) => theme.spacing.medium};
    flex-wrap: wrap;
    justify-content: center;

    @keyframes slideUp {
      from {
        transform: translateY(100%);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }
  }
`

export const BulkSelectionInfo = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};

  ${media.tabletDown} {
    font-size: 10px;
    flex-basis: 100%;
    text-align: center;
  }
`

export const BulkActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};

  ${media.tabletDown} {
    gap: ${({ theme }) => theme.spacing.xsmall};
  }
`

export const BulkActionBtn = styled.button<{
  $variant?: 'primary' | 'secondary' | 'danger'
}>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};

  svg {
    width: 13px;
    height: 13px;
  }

  ${media.tabletDown} {
    padding: ${({ theme }) => theme.spacing.xsmall}
      ${({ theme }) => theme.spacing.small};
    font-size: 10px;

    svg {
      width: 12px;
      height: 12px;
    }
  }

  ${({ $variant = 'secondary', theme }) => {
    switch ($variant) {
      case 'primary':
        return css`
          background: ${theme.colors.primary};
          color: ${theme.colors.text.inverse};
          border: 1px solid ${theme.colors.primary};

          &:hover:not(:disabled) {
            background: ${theme.colors.primary};
            border-color: ${theme.colors.primary};
          }
        `
      case 'danger':
        return css`
          background: ${theme.colors.background.primary};
          color: ${theme.colors.danger};
          border: 1px solid ${theme.colors.danger};

          &:hover:not(:disabled) {
            background: ${theme.colors.danger};
            color: ${theme.colors.text.inverse};
          }
        `
      default:
        return css`
          background: ${theme.colors.background.primary};
          color: ${theme.colors.text.primary};
          border: 1px solid ${theme.colors.border.default};

          &:hover:not(:disabled) {
            background: ${theme.colors.background.secondary};
            border-color: ${theme.colors.text.secondary};
          }
        `
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

// Empty State
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xlarge};
  text-align: center;
`

export const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  margin-bottom: ${({ theme }) => theme.spacing.large};
  color: ${({ theme }) => theme.colors.text.tertiary};

  svg {
    width: 100%;
    height: 100%;
  }
`

export const EmptyTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.small};
`

export const EmptyDescription = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  max-width: 320px;
`

// Resize and Drag handles
export const ResizeHandle = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 10px;
  cursor: col-resize;
  user-select: none;
  z-index: 2;
  transform: translateX(50%);

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 2px;
    background: transparent;
    transform: translateX(-50%);
    transition: all 0.2s;
  }

  &:hover::before {
    background: ${({ theme }) => theme.colors.primary};
    width: 3px;
  }

  &:active::before {
    background: ${({ theme }) => theme.colors.primary};
    width: 4px;
  }
`

export const DragHandle = styled.button.attrs({ type: 'button' })`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: grab;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.3;
  transition: opacity ${({ theme }) => theme.transitions.default};
  z-index: 1;

  svg {
    width: 12px;
    height: 12px;
    color: ${({ theme }) => theme.colors.text.tertiary};
  }

  th:hover & {
    opacity: 1;

    svg {
      color: ${({ theme }) => theme.colors.text.secondary};
    }
  }

  &:active {
    cursor: grabbing;
  }
`

export const DragPreview = styled.div`
  position: fixed;
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: 10px;
  font-weight: 600;
  pointer-events: none;
  z-index: 9999;
  box-shadow: ${({ theme }) => theme.shadows.lg};
`

export const DropIndicator = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 3px;
  background: ${({ theme }) => theme.colors.primary};
  z-index: 10;
  pointer-events: none;
  border-radius: 2px;
  box-shadow: 0 0 4px ${({ theme }) => theme.colors.primary};
`

// Detail Panel
export const DetailPanel = styled.div<{
  $isOpen: boolean
  $width?: string | number
}>`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: ${({ $width }) =>
    typeof $width === 'number' ? `${$width}px` : $width || '600px'};
  background: ${({ theme }) => theme.colors.background.primary};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '100%')});
  transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  z-index: 1000;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  ${media.tabletDown} {
    width: 70%;
  }

  ${media.mobileOnly} {
    width: 100%;
  }
`

export const DetailPanelHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.large};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const DetailPanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.large};
`

export const DetailPanelClose = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  transition: all ${({ theme }) => theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  /* Mobile: Ensure minimum 44px touch target */
  @media (max-width: 1023px) {
    width: ${({ theme }) => theme.touchTargets.minimum};
    height: ${({ theme }) => theme.touchTargets.minimum};
  }

  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover {
      background: none;
      color: ${({ theme }) => theme.colors.text.secondary};
    }

    &:active {
      background: ${({ theme }) => theme.colors.background.secondary};
      color: ${({ theme }) => theme.colors.text.primary};
    }
  }
`

// ============================================
// Card-based Mobile View Components
// ============================================

/**
 * Container for card-based mobile view
 * Only visible on mobile when mobileView='cards'
 */
export const CardViewContainer = styled.div`
  display: none;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  padding: 0;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: flex;
  }
`

/**
 * Individual card for a data row
 */
export const DataCard = styled.div<{ $selected?: boolean }>`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid
    ${({ $selected, theme }) =>
      $selected ? theme.colors.primary : theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  transition: all ${({ theme }) => theme.transitions.default};
  box-shadow: none;

  ${({ $selected, theme }) =>
    $selected &&
    css`
      background: ${theme.colors.primaryLight};
      border-color: ${theme.colors.primary};
      box-shadow:
        0 2px 8px rgba(0, 173, 230, 0.2),
        0 1px 3px rgba(0, 0, 0, 0.08);
    `}

  &:active {
    transform: scale(0.99);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  /* Disable hover effects on touch devices */
  @media (hover: hover) {
    &:hover {
      border-color: ${({ theme }) => theme.colors.primary};
      box-shadow:
        0 4px 12px rgba(0, 0, 0, 0.1),
        0 2px 4px rgba(0, 0, 0, 0.06);
    }
  }
`

/**
 * Card header with title and selection checkbox
 */
export const DataCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  min-height: 48px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.secondary};
`

/**
 * Checkbox container in card header
 */
export const CardCheckbox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;

  input[type='checkbox'] {
    width: 15px;
    height: 15px;
    cursor: pointer;
    accent-color: ${({ theme }) => theme.colors.primary};
  }
`

/**
 * Card title (usually first column value)
 */
export const DataCardTitle = styled.div`
  flex: 1;
  font-size: ${({ theme }) => theme.typography.fontSize.title};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  min-width: 0;
  overflow: visible;
  overflow-wrap: anywhere;
  line-height: 1.35;

  /* Renderers often return an inline flex row containing a name and badge.
     Let that row wrap instead of clipping long companies, users or offices. */
  & > * {
    min-width: 0;
    max-width: 100%;
    flex-wrap: wrap;
    overflow-wrap: anywhere;
  }
`

/**
 * Card body with key-value pairs
 */
export const DataCardBody = styled.div`
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
`

/**
 * Key-value row in card body
 */
export const DataCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  min-height: 26px;
  padding: 2px 0;
`

/**
 * Label for a card field
 */
export const DataCardLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.tertiary};
  flex-shrink: 0;
  min-width: 80px;
  max-width: 42%;
  overflow-wrap: anywhere;
`

/**
 * Value for a card field
 */
export const DataCardValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: right;
  word-break: break-word;
  flex: 1;
  min-width: 0;
  overflow: visible;
  line-height: 1.4;

  /* Handle complex values with sub-content */
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;

  & > * {
    max-width: 100%;
  }

  /* Style sub-content (usually smaller text below main value) */
  & > span:not(:first-child),
  & > div:not(:first-child) {
    font-size: ${({ theme }) => theme.typography.fontSize.small};
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

/**
 * Card footer with actions
 */
export const DataCardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.secondary};

  /* Ensure action buttons have adequate touch targets */
  & > button {
    min-height: ${({ theme }) => theme.touchTargets.minimum};
  }
`

/**
 * Wrapper to hide table on mobile when using card view
 */
export const TableViewWrapper = styled.div<{ $hideOnMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  ${({ $hideOnMobile }) =>
    $hideOnMobile &&
    css`
      @media (max-width: 639px) {
        display: none;
      }
    `}
`

// ============================================
// Swipeable Card Components
// ============================================

/**
 * Container for swipeable card - positions actions behind content
 */
export const SwipeableCardContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
`

/**
 * Container for swipe action buttons
 */
export const SwipeActionsContainer = styled.div<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 0;
  bottom: 0;
  ${({ $side }) => ($side === 'left' ? 'left: 0;' : 'right: 0;')}
  display: flex;
  align-items: stretch;
`

/**
 * Individual swipe action button
 */
export const SwipeActionButton = styled.button<{ $color?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  width: 64px;
  background: ${({ $color, theme }) => $color || theme.colors.primary};
  border: none;
  color: white;
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  svg {
    width: 16px;
    height: 16px;
  }

  &:active {
    filter: brightness(0.9);
  }
`

/**
 * Content wrapper that slides to reveal actions
 */
export const SwipeableCardContent = styled.div<{ $translateX: number }>`
  transform: translateX(${({ $translateX }) => $translateX}px);
  background: ${({ theme }) => theme.colors.background.primary};
  position: relative;
  z-index: 1;
`

/**
 * Wrapper for expandable content in card
 */
export const DataCardExpandableContent = styled.div<{ $expanded?: boolean }>`
  display: flex;
  flex-direction: column;
`

/**
 * Expandable section in card body (for showing more fields)
 */
export const DataCardExpandableInner = styled.div<{ $expanded: boolean }>`
  overflow: hidden;
  max-height: ${({ $expanded }) => ($expanded ? '500px' : '0')};
  opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
  transition: all ${({ theme }) => theme.transitions.default};

  /* Inner content wrapper */
  & > div {
    padding: ${({ theme }) => theme.spacing.small}
      ${({ theme }) => theme.spacing.medium};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.xsmall};
    border-top: 1px solid ${({ theme }) => theme.colors.border.light};
    background: ${({ theme }) => theme.colors.background.secondary};
  }
`

/**
 * Button to expand/collapse card fields
 */
export const DataCardExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.xsmall};
  background: ${({ theme }) => theme.colors.background.secondary};
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  cursor: pointer;
  min-height: 36px;
  transition: all ${({ theme }) => theme.transitions.default};

  svg {
    width: 14px;
    height: 14px;
    transition: transform ${({ theme }) => theme.transitions.default};
  }

  &[aria-expanded='true'] svg {
    transform: rotate(180deg);
  }

  &:active {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }
`

// ============================================
// Pull-to-Refresh Components
// ============================================

/**
 * Wrapper for pull-to-refresh functionality
 */
export const PullToRefreshWrapper = styled.div`
  position: relative;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  width: 100%;
  touch-action: pan-y;
  display: flex;
  flex-direction: column;
`

/**
 * Pull indicator shown at top during pull gesture
 */
export const PullIndicator = styled.div<{
  $pullDistance: number
  $canRelease: boolean
  $isRefreshing: boolean
}>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: ${({ $pullDistance }) => $pullDistance}px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.background.secondary};
  overflow: hidden;
  z-index: 5;
`

/**
 * Content inside pull indicator
 */
export const PullIndicatorContent = styled.div<{
  $progress: number
  $isRefreshing: boolean
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  opacity: ${({ $progress }) => Math.min($progress, 1)};
  transform: scale(${({ $progress }) => 0.5 + Math.min($progress, 1) * 0.5});
  transition: ${({ $isRefreshing }) =>
    $isRefreshing ? 'none' : 'transform 0.1s ease-out'};

  svg {
    color: ${({ theme }) => theme.colors.primary};
    transition: transform 0.2s ease;
    ${({ $isRefreshing }) =>
      $isRefreshing &&
      css`
        animation: spin 1s linear infinite;
      `}
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

/**
 * Text inside pull indicator
 */
export const PullIndicatorText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`

/**
 * Content that moves down when pulling
 */
export const PullableContent = styled.div<{ $translateY: number }>`
  transform: translateY(${({ $translateY }) => $translateY}px);
  transition: ${({ $translateY }) =>
    $translateY === 0 ? 'transform 0.3s ease-out' : 'none'};
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
`
